import { createPerformanceObserver } from './createPerformanceObserver';
import { getTimeFromNavigationStart } from './getTimeFromNavigationStart';
import { trace } from 'owa-trace';

const performanceResourceTimingEntries: {
    [key: string]: PerformanceResourceTiming[];
} = {};

/**
 * Helper function that creates a performanceObserver and waits
 * for performanceEntry associated with the given module
 * @param moduleName
 */
export function registerModuleWithPerformanceObserver(moduleName: string) {
    try {
        const resourceTimingObserver = createPerformanceObserver('resource', list => {
            list.getEntries().forEach((entry: PerformanceResourceTiming) => {
                const adjustedModuleRegExp = new RegExp(`.*[~.]+${moduleName}[~.]+.*`);

                if (adjustedModuleRegExp.test(entry.name)) {
                    if (!performanceResourceTimingEntries[moduleName]) {
                        performanceResourceTimingEntries[moduleName] = [];
                    }

                    performanceResourceTimingEntries[moduleName].push(entry);
                }
            });
        });

        resourceTimingObserver?.observe({ type: 'resource', buffered: true });
    } catch (e) {
        trace.warn('Error while creating the performance observer' + e);
    }
}

/**
 * Helper function which filters the entries for prefetch tags.
 * @param moduleName
 */
function getEntriesForModule(moduleName: string) {
    const entriesForModule = performanceResourceTimingEntries[moduleName];

    // Filtering out entries initiated via prefetch tags to get timings incurred when initiated from script.
    return entriesForModule
        ? entriesForModule.filter(entry => entry.initiatorType === 'script')
        : null;
}

/**
 * Helper function that returns the ResourceTimingEntry associated
 * with the given module.
 * Returns the time taken by largest bundle for the module, including
 * its vendor bundles.
 * @param moduleName
 */
export function getDurationForModule(moduleName: string) {
    const entriesForModule = getEntriesForModule(moduleName);
    return entriesForModule ? Math.max(...entriesForModule.map(entry => entry.duration)) : 0;
}

/**
 * Helper function that returns bundle load start and end markers for the module
 * @param moduleName
 * @returns
 */
export function getStartEndTimeStampForModule(moduleName: string) {
    const entriesForModule = getEntriesForModule(moduleName);
    const effectiveStartEndTimings = {
        bls: 0,
        ble: 0,
    };

    if (entriesForModule) {
        effectiveStartEndTimings.bls = Math.round(
            Math.min(...entriesForModule.map(entry => entry.fetchStart))
        );
        effectiveStartEndTimings.ble = Math.round(
            Math.max(...entriesForModule.map(entry => entry.responseEnd))
        );
    }

    return effectiveStartEndTimings;
}

/**
 * Helper function that returns the CacheStatus for module
 * @param moduleName
 */
export function getCacheStatusForModule(moduleName: string) {
    const entriesForModule = getEntriesForModule(moduleName);
    return entriesForModule
        ? entriesForModule.every(entry => entry.transferSize === 0 && entry.decodedBodySize >= 0)
        : false;
}

/**
 * Helper function that returns the parse timings for module
 * @param moduleName
 */
export function getParseTimingsForModule(moduleName: string) {
    const entriesForModule = getEntriesForModule(moduleName);
    const effectiveParseTime = {
        ps: 0,
        pe: 0,
    };

    if (entriesForModule) {
        const parseTimings = entriesForModule.map(entry => {
            // PerformanceResourceEntry consists of the full module name along with CDN prefix.
            // Splitting the same to get the adjusted module name
            const adjustedModuleName = entry.name?.split('/')?.slice(-1)?.[0];
            return {
                // All the timestamps in the PerformanceResourceEntry reference wrt to NavigationStart.
                // where as the values in window.scriptProcessStart or window.scriptProcessEnd are DateTimeStamps.
                // Hence adjusting them to calculate the time from navigation start.
                parseStart:
                    getTimeFromNavigationStart(window.scriptProcessStart?.[adjustedModuleName]) ??
                    0,
                parseEnd:
                    getTimeFromNavigationStart(window.scriptProcessEnd?.[adjustedModuleName]) ?? 0,
            };
        });

        effectiveParseTime.ps = Math.round(
            Math.min(...parseTimings.map(module => module.parseStart))
        );
        effectiveParseTime.pe = Math.round(
            Math.max(...parseTimings.map(module => module.parseEnd))
        );
    }

    return effectiveParseTime;
}
